export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const splitStream = (splitOn: string) => {
	let buffer = '';
	return new TransformStream<string, string>({
		transform(chunk, controller) {
			buffer += chunk;
			const parts = buffer.split(splitOn);
			parts.slice(0, -1).forEach((part) => controller.enqueue(part));
			buffer = parts[parts.length - 1];
		},
		flush(controller) {
			if (buffer) {
				controller.enqueue(buffer);
			}
		}
	});
};

export const compareVersion = (latest: string, current: string) =>
	current === '0.0.0'
		? false
		: current.localeCompare(latest, undefined, {
				numeric: true,
				sensitivity: 'case',
				caseFirst: 'upper'
			}) < 0;

export const formatFileSize = (size: unknown) => {
	if (size == null) {
		return 'Unknown size';
	}
	if (typeof size !== 'number' || size < 0) {
		return 'Invalid size';
	}
	if (size === 0) {
		return '0 B';
	}

	const units = ['B', 'KB', 'MB', 'GB', 'TB'];
	let unitIndex = 0;
	let normalizedSize = size;

	while (normalizedSize >= 1024 && unitIndex < units.length - 1) {
		normalizedSize /= 1024;
		unitIndex++;
	}

	return `${normalizedSize.toFixed(1)} ${units[unitIndex]}`;
};

const HEIC_LIKE_IMAGE_EXTENSION_RE = /\.(?:heic|heif|heics|heifs|hesc)$/i;
const HEIC_LIKE_IMAGE_MIME_TYPES = new Set([
	'image/heic',
	'image/heif',
	'image/heic-sequence',
	'image/heif-sequence'
]);

export const isHeicLikeImageFile = (file: Pick<File, 'name' | 'type'>) => {
	const mimeType = String(file.type || '').toLowerCase();
	return HEIC_LIKE_IMAGE_MIME_TYPES.has(mimeType) || HEIC_LIKE_IMAGE_EXTENSION_RE.test(file.name);
};

export const isPortalImageFile = (file: Pick<File, 'name' | 'type'>) =>
	String(file.type || '').startsWith('image/') || isHeicLikeImageFile(file);

export const normalizeHeicLikeImageFilename = (name: string) =>
	String(name || 'image.heic').replace(HEIC_LIKE_IMAGE_EXTENSION_RE, '.jpg');

const convertBrowserReadableImageToJpeg = (file: File): Promise<Blob> =>
	new Promise((resolve, reject) => {
		if (typeof document === 'undefined' || typeof URL === 'undefined') {
			reject(new Error('Browser image conversion is unavailable.'));
			return;
		}

		const imageUrl = URL.createObjectURL(file);
		const img = new Image();
		const cleanup = () => URL.revokeObjectURL(imageUrl);

		img.onload = () => {
			try {
				const canvas = document.createElement('canvas');
				canvas.width = img.naturalWidth || img.width;
				canvas.height = img.naturalHeight || img.height;
				if (!canvas.width || !canvas.height) {
					throw new Error('Image dimensions are unavailable.');
				}
				const context = canvas.getContext('2d');
				if (!context) {
					throw new Error('Canvas image conversion is unavailable.');
				}
				context.drawImage(img, 0, 0, canvas.width, canvas.height);
				canvas.toBlob(
					(blob) => {
						cleanup();
						if (blob) {
							resolve(blob);
							return;
						}
						reject(new Error('Browser image conversion failed.'));
					},
					'image/jpeg',
					0.92
				);
			} catch (err) {
				cleanup();
				reject(err);
			}
		};
		img.onerror = () => {
			cleanup();
			reject(new Error('Browser cannot decode this HEIC image.'));
		};
		img.src = imageUrl;
	});

let resizeImageWarmupDone = false;

const resizeImageToDataURL = async (
	img: HTMLImageElement,
	width: number,
	height: number,
	mimeType = 'image/jpeg'
): Promise<string> => {
	const canvas = document.createElement('canvas');
	canvas.width = width;
	canvas.height = height;
	canvas.getContext('2d')?.drawImage(img, 0, 0, width, height);

	const toDataURL = () => canvas.toDataURL(mimeType);

	if (
		!resizeImageWarmupDone &&
		canvas.toBlob &&
		/android|iphone|ipad|ipod/i.test(navigator?.userAgent)
	) {
		resizeImageWarmupDone = true;
		return new Promise((resolve) => {
			canvas.toBlob((blob) => {
				if (!blob) {
					resolve(toDataURL());
					return;
				}
				const reader = new FileReader();
				reader.onload = () => resolve(String(reader.result));
				reader.onerror = () => resolve(toDataURL());
				reader.readAsDataURL(blob);
			}, mimeType);
		});
	}

	return toDataURL();
};

export const compressImage = async (
	imageUrl: string,
	maxWidth?: number,
	maxHeight?: number
): Promise<string> =>
	new Promise<string>((resolve, reject) => {
		const img = new Image();
		img.onload = async () => {
			let width = img.width;
			let height = img.height;

			if (maxWidth && maxHeight) {
				if (width <= maxWidth && height <= maxHeight) {
					resolve(imageUrl);
					return;
				}

				if (width / height > maxWidth / maxHeight) {
					height = Math.round((maxWidth * height) / width);
					width = maxWidth;
				} else {
					width = Math.round((maxHeight * width) / height);
					height = maxHeight;
				}
			} else if (maxWidth) {
				if (width <= maxWidth) {
					resolve(imageUrl);
					return;
				}

				height = Math.round((maxWidth * height) / width);
				width = maxWidth;
			} else if (maxHeight) {
				if (height <= maxHeight) {
					resolve(imageUrl);
					return;
				}

				width = Math.round((maxHeight * width) / height);
				height = maxHeight;
			}

			const mimeType = imageUrl.match(/^data:([^;]+);/)?.[1] ?? 'image/jpeg';
			resolve(await resizeImageToDataURL(img, width, height, mimeType));
		};
		img.onerror = (error) => reject(error);
		img.src = imageUrl;
	});

export const convertHeicToJpeg = async (file: File) => {
	try {
		return await convertBrowserReadableImageToJpeg(file);
	} catch {
		// Continue with WASM converters below.
	}

	try {
		const { heicTo } = await import('heic-to/csp');
		return await heicTo({ blob: file, type: 'image/jpeg', quality: 0.92 });
	} catch {
		// Older browsers can still work with the legacy converter.
	}

	try {
		const { default: heic2any } = await import('heic2any');
		const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
		const blob = converted instanceof Blob ? converted : Array.isArray(converted) ? converted[0] : null;
		if (!blob) {
			throw new Error('HEIC conversion returned no image.');
		}
		return blob;
	} catch (err: unknown) {
		if (err instanceof Error && err.message.includes('already browser readable')) {
			return convertBrowserReadableImageToJpeg(file);
		}
		throw new Error(
			`${file.name || 'This HEIC image'} could not be converted. Please resave it as JPEG or PNG and try again.`
		);
	}
};
